import type { AssessmentTemplate } from '../../model/assessments/interfaces'
import type { AssessmentTemplatePayload } from '../../model/assessments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { toAssessmentTemplate } from './template-response'

interface AssessmentTemplateUpdateApiOptions {
  templateId: string | number
  /** The WORKING DOCUMENT payload — never Zod's parsed output (B9). `PATCH` is a full replacement
   *  (§4), so every field must be sent every save. */
  payload: AssessmentTemplatePayload
  onStart?: () => void
  onSuccess?: (template: AssessmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `PATCH /admin-api/assessment-templates/{template_id}` — update (contract §4). A full
 * replacement, not a partial patch; the response is normalised as `show` (B7).
 */
export const useAssessmentTemplateUpdateApi = async (
  options: AssessmentTemplateUpdateApiOptions,
): Promise<AssessmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assessment-templates.update', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.patch<ApiResponse>(url, options.payload)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template = toAssessmentTemplate(data.data)

    if (!template) {
      const msg = 'Unexpected error. The assessment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    toast.success('Assessment template updated')
    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
